import { Tent, Layers } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

interface CampsGuideOverviewProps {
  className?: string;
}

/** Camps page overview — shared by the page help popover and the empty state. */
export function CampsGuideOverview({ className }: CampsGuideOverviewProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <p className="text-sm text-muted-foreground">
        Use this page to set up multi-day holiday and summer camps. Camps are ideal for charging
        students on a daily or weekly basis—families can book by individual day or for the full camp
        package on your public academy page.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-left">
          <div className="mb-1.5 flex items-center gap-2">
            <Tent className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="text-sm font-medium text-foreground">Single camp</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            One bookable camp with its own dates, pricing, coaches, and capacity. Good for a single
            holiday week or standalone intensive.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-muted/40 p-3 text-left">
          <div className="mb-1.5 flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" aria-hidden />
            <h4 className="text-sm font-medium text-foreground">Multiple camps</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            Use the wizard to create several camps at once with a shared name and schedule—optionally
            split by program or age group each week. Each camp is booked individually on your public page.
          </p>
        </div>
      </div>
    </div>
  );
}

interface CampsGuideHelpContentProps {
  onStartTour: () => void;
}

/** Rich help content for the Camps page — shown in the top-bar Page help popover. */
export function CampsGuideHelpContent({ onStartTour }: CampsGuideHelpContentProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-foreground">Creating camps</h3>
        <CampsGuideOverview />
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onStartTour}>
        Take a quick tour
      </Button>
    </div>
  );
}
